import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  ListOrdered,
  Timer,
  Truck,
  PenTool,
  Shield,
  Mail,
  FileText,
  Building2,
  Heart,
  Users,
  Bell,
  FlaskConical,
  X,
} from 'lucide-react';
import { useImpersonate } from '../../contexts/ImpersonateContext';
import type { LucideIcon } from 'lucide-react';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  access: 'all' | 'admin' | 'marketing';
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, access: 'admin' },
  { name: 'Kolejka wystawiania', href: '/queue', icon: ListOrdered, access: 'admin' },
  { name: 'Wydajnosc', href: '/performance', icon: Timer, access: 'admin' },
  { name: 'Dostarczalnosc', href: '/delivery', icon: Truck, access: 'admin' },
  { name: 'Podpisywanie', href: '/signing', icon: PenTool, access: 'admin' },
  { name: 'Pieczeci', href: '/seals', icon: Shield, access: 'admin' },
  { name: 'Szablony email', href: '/email-templates', icon: Mail, access: 'marketing' },
  { name: 'Szablony certyfikatow', href: '/certificate-templates', icon: FileText, access: 'marketing' },
  { name: 'Organizacje', href: '/organisations', icon: Building2, access: 'admin' },
  { name: 'Test wystawiania', href: '/issuance-test', icon: FlaskConical, access: 'admin' },
];

const secondaryNav: NavItem[] = [
  { name: 'Uzytkownicy', href: '/users', icon: Users, access: 'admin' },
  { name: 'Alerty', href: '/alerting', icon: Bell, access: 'admin' },
  { name: 'Status systemu', href: '/health', icon: Heart, access: 'admin' },
];

interface SidebarProps {
  /** Desktop: icons-only rail instead of the full-width panel. */
  collapsed: boolean;
  /** Mobile: whether the off-canvas drawer is showing. */
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function Sidebar({ collapsed, mobileOpen, onCloseMobile }: SidebarProps) {
  const { effectiveIsAdmin, effectiveIsMarketing } = useImpersonate();

  const canSee = (item: NavItem) => {
    if (item.access === 'all') return true;
    if (item.access === 'admin') return effectiveIsAdmin;
    if (item.access === 'marketing') return effectiveIsMarketing;
    return false;
  };

  const visibleNav = navigation.filter(canSee);
  const visibleSecondary = secondaryNav.filter(canSee);

  return (
    <aside
      className={clsx(
        'bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col h-full',
        // Below lg the sidebar is an overlay drawer so it never squeezes the page;
        // from lg up it is a normal flex child that can shrink to an icon rail.
        'fixed inset-y-0 left-0 z-40 w-64 transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 lg:transition-[width]',
        mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
        collapsed ? 'lg:w-16' : 'lg:w-64'
      )}
    >
      <div className={clsx('border-b border-gray-200 dark:border-gray-800', collapsed ? 'p-6 lg:p-3' : 'p-6')}>
        <div className={clsx('flex items-center gap-3', collapsed && 'lg:justify-center lg:gap-0')}>
          <div className="p-2 bg-brand-600 rounded-lg shrink-0">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div className={clsx('min-w-0', collapsed && 'lg:hidden')}>
            <h1 className="text-sm font-bold text-gray-900 dark:text-white truncate">Credentium</h1>
            <p className="text-xs text-gray-500">Management</p>
          </div>
          <button
            onClick={onCloseMobile}
            className="ml-auto p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden"
            aria-label="Zamknij menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {visibleNav.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.href === '/'}
            onClick={onCloseMobile}
            title={collapsed ? item.name : undefined}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                collapsed && 'lg:justify-center lg:px-0',
                isActive
                  ? 'bg-brand-50 text-brand-700 border border-brand-200 dark:bg-brand-600/10 dark:text-brand-400 dark:border-brand-600/20'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
              )
            }
          >
            <item.icon className="h-4.5 w-4.5 shrink-0" />
            <span className={clsx('truncate', collapsed && 'lg:hidden')}>{item.name}</span>
          </NavLink>
        ))}
      </nav>

      {visibleSecondary.length > 0 && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-800 space-y-1">
          {visibleSecondary.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={onCloseMobile}
              title={collapsed ? item.name : undefined}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors',
                  collapsed && 'lg:justify-center lg:px-0',
                  isActive
                    ? 'text-brand-700 dark:text-brand-400'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                )
              }
            >
              <item.icon className="h-3.5 w-3.5 shrink-0" />
              <span className={clsx('truncate', collapsed && 'lg:hidden')}>{item.name}</span>
            </NavLink>
          ))}
        </div>
      )}
    </aside>
  );
}
